'use client'

import { createContext, type ReactNode, useContext } from 'react'

import type { UseElementEditTrackerResult } from './element-edit-tracker'

/**
 * Internal context exposing the element-edit tracker (start / complete).
 * Consumed by the canvas editor
 * surface which fires the `POST /api/element-edit` request directly;
 * UI components that only need to read in-flight state use the
 * simpler `useCanvasCallbacks` API.
 *
 * The provider is mounted by the layout-level `GlobalChatProvider`
 * (so both the global chat composer and the emails canvas share one
 * tracker instance). The legacy path
 * `app/(app)/(workspace)/emails/(canvas)/canvas-element-edit-context.tsx`
 * re-exports from here for back-compat.
 *
 * The tracker is purely UI state — no queueing, no per-email serial
 * dispatch. Multiple in-flight edits coexist; the underlying endpoint
 * runs them in parallel and uses optimistic-concurrency at save time
 * to keep email versions race-safe.
 */
const ElementEditTrackerContext =
  createContext<UseElementEditTrackerResult | null>(null)

export function ElementEditTrackerProvider({
  tracker,
  children,
}: {
  tracker: UseElementEditTrackerResult
  children: ReactNode
}) {
  return (
    <ElementEditTrackerContext.Provider value={tracker}>
      {children}
    </ElementEditTrackerContext.Provider>
  )
}

export function useElementEditTrackerContext(): UseElementEditTrackerResult {
  const ctx = useContext(ElementEditTrackerContext)
  if (!ctx) {
    throw new Error(
      'useElementEditTrackerContext must be used within ElementEditTrackerProvider'
    )
  }
  return ctx
}
